import { supabase } from '../lib/supabase';
import { sendNotificationEmail } from './emailService';

export type NotificationPreference = {
  comments: boolean;
  approvals: boolean;
  files: boolean;
};

export type NotificationRecord = {
  id: string;
  user_id: string;
  type: string;
  message: string;
  project_id?: string;
  channel: 'in_app' | 'email';
  created_at?: string;
  is_read?: boolean;
};

const defaultPreferences: NotificationPreference = {
  comments: true,
  approvals: true,
  files: false,
};

export async function getNotificationPreferences(userId: string): Promise<NotificationPreference> {
  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error || !data) {
    return defaultPreferences;
  }

  return {
    comments: data.comments_enabled ?? defaultPreferences.comments,
    approvals: data.approvals_enabled ?? defaultPreferences.approvals,
    files: data.files_enabled ?? defaultPreferences.files,
  };
}

export async function saveNotificationPreferences(userId: string, prefs: NotificationPreference) {
  const { error } = await supabase
    .from('notification_preferences')
    .upsert({
      user_id: userId,
      comments_enabled: prefs.comments,
      approvals_enabled: prefs.approvals,
      files_enabled: prefs.files,
    });

  if (error) {
    throw error;
  }
}

export async function createNotification(
  userId: string,
  payload: { type: string; message: string; projectId?: string; sendEmail?: boolean }
): Promise<void> {
  const { error } = await supabase.from('notifications').insert({
    user_id: userId,
    type: payload.type,
    message: payload.message,
    project_id: payload.projectId,
    channel: payload.sendEmail ? 'email' : 'in_app',
  });

  if (error) {
    console.error('Failed to create notification', error);
  }

  if (payload.sendEmail) {
    await sendNotificationEmail({
      to: (await supabase.auth.getUser()).data.user?.email,
      subject: 'New activity on Verline',
      body: payload.message,
    });
  }
}

export async function listNotifications(userId: string): Promise<NotificationRecord[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error || !data) return [];
  return data as NotificationRecord[];
}

/**
 * Mark a notification as read
 */
export async function markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId)
    .eq('user_id', userId);

  if (error) {
    console.error('Failed to mark notification as read', error);
    throw error;
  }
}

/**
 * Mark all notifications as read for a user
 */
export async function markAllNotificationsAsRead(userId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('Failed to mark all notifications as read', error);
    throw error;
  }
}

